import {
    buildHierarchyFlatOrder,
    getInheritedDeadline,
    getInheritedTimeline,
    getChildren,
} from './taskHierarchyUtils';
import {
    formatDuration,
    getStatusDisplayMeta,
    getVisibleTaskTags,
} from './taskSchedulingUtils';
import { formatPlainText, stripHtmlToPlainText } from './pdfTextUtils';

const PRIORITY_LABELS = {
    HIGH: 'High',
    MEDIUM: 'Medium',
    LOW: 'Low',
    '': 'None',
    NONE: 'None',
};

const formatShareDate = value => {
    if (!value) return '';
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    } catch {
        return '';
    }
};

const getOwnerName = (task, projectUsersAll) => {
    const userId = task?.assigned_to || task?.created_by;
    if (!userId) {
        const offline = (task?.assigned_to_offline || '').trim();
        return offline || '';
    }
    const match = projectUsersAll?.find(user => user?.value?.user_id === userId);
    return formatPlainText(match?.value?.full_name || '');
};

const taskHasChildren = (task, allTasks) => getChildren(task?.id, allTasks).length > 0;

const getDeadlineValue = (task, allTasks) => {
    if (taskHasChildren(task, allTasks)) {
        return task.deadline || getInheritedDeadline(task, allTasks).deadline;
    }
    return task.deadline;
};

const orderSelectedTasks = (allTasks, selectedIds, customOrder) => {
    const idSet = new Set(selectedIds.map(id => String(id)));
    const orderedIds = buildHierarchyFlatOrder(allTasks, customOrder, null);
    const taskMap = new Map(allTasks.map(task => [String(task.id), task]));
    return orderedIds.map(id => taskMap.get(String(id))).filter(task => task && idSet.has(String(task.id)));
};

const escapeHtml = value =>
    String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

export const htmlToPlainText = html =>
    (html || '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(h1|h2|h3|p|li|div)>/gi, '\n')
        .replace(/<\/(ul|ol)>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

export const isShareUpdateEmpty = html => !htmlToPlainText(html);

const formatTaskBlock = (task, index, context) => {
    const { allTasks, projectUsersAll, selectedProject } = context;
    const parts = [];

    const taskName = formatPlainText(task.task || '');
    if (taskName) {
        parts.push(`<h2>${escapeHtml(`${index}. ${taskName}`)}</h2>`);
    }

    const owner = getOwnerName(task, projectUsersAll);
    if (owner) parts.push(`<p><strong>Owner:</strong> ${escapeHtml(owner)}</p>`);

    const priority = PRIORITY_LABELS[task.priority] || '';
    if (priority && priority !== 'None') {
        parts.push(`<p><strong>Priority:</strong> ${escapeHtml(priority)}</p>`);
    }

    const statusMeta = getStatusDisplayMeta(task.status, selectedProject);
    if (statusMeta?.label) {
        parts.push(`<p><strong>Status:</strong> ${escapeHtml(statusMeta.label)}</p>`);
    }

    const deadlineValue = getDeadlineValue(task, allTasks);
    const deadlineText = formatShareDate(deadlineValue);
    if (deadlineText) {
        parts.push(`<p><strong>Deadline:</strong> ${escapeHtml(deadlineText)}</p>`);
    }

    const timeline = getInheritedTimeline(task, allTasks);
    const start = formatShareDate(timeline?.start_date);
    const end = formatShareDate(timeline?.end_date);
    if (start || end) {
        const timelineText = start && end ? `${start} - ${end}` : start || end;
        parts.push(`<p><strong>Timeline:</strong> ${escapeHtml(timelineText)}</p>`);
    }

    if (task.duration != null) {
        const durationText = formatDuration(task.duration);
        if (durationText) {
            parts.push(`<p><strong>Duration:</strong> ${escapeHtml(durationText)}</p>`);
        }
    }

    if (task.workstream_repr) {
        parts.push(
            `<p><strong>Workstream:</strong> ${escapeHtml(formatPlainText(task.workstream_repr))}</p>`,
        );
    }

    if (task.project_phase) {
        parts.push(
            `<p><strong>Project Phase:</strong> ${escapeHtml(formatPlainText(task.project_phase))}</p>`,
        );
    }

    const tags = getVisibleTaskTags(task)
        .map(formatPlainText)
        .filter(Boolean);
    if (tags.length) {
        parts.push(`<p><strong>Tags:</strong> ${escapeHtml(tags.join(', '))}</p>`);
    }

    const description = stripHtmlToPlainText(task.description);
    if (description) {
        parts.push(`<p><strong>Description:</strong> ${escapeHtml(description)}</p>`);
    }

    if (task.parent_task) {
        const parent = allTasks.find(t => t.id === task.parent_task);
        const parentName = formatPlainText(parent?.task || '');
        if (parentName) {
            parts.push(`<p><strong>Parent:</strong> ${escapeHtml(parentName)}</p>`);
        }
    }

    return parts.join('');
};

/**
 * Format selected tasks as a deterministic HTML update (no AI).
 */
export const formatShareUpdateAsSelected = ({
    tasks = [],
    selectedIds = [],
    customOrder = null,
    selectedProject = null,
    projectUsersAll = [],
}) => {
    const allTasks = tasks || [];
    const ordered = orderSelectedTasks(allTasks, selectedIds, customOrder);
    if (!ordered.length) return '';

    const projectName = formatPlainText(selectedProject?.project_name || 'Project Update');
    const header = `${projectName} — Task Update`;

    const context = { allTasks, projectUsersAll, selectedProject };
    const blocks = ordered.map((task, idx) => formatTaskBlock(task, idx + 1, context));

    return `<h1>${escapeHtml(header)}</h1>${blocks.join('')}`.trim();
};

export const buildDefaultEmailSubject = projectName => {
    const name = formatPlainText(projectName || 'Project Update');
    const dateStr = formatShareDate(new Date());
    return `${name} — Task Update — ${dateStr}`;
};

export default formatShareUpdateAsSelected;
